<template>
  <div class="duplicate-logs-details">
    <b-loading :active="isLoading"></b-loading>

    <OmxPageHeader
        :title="pageTitle"
        :subtitle="$t('duplicate-logs-details-view.more-info-text')"
    />

    <OmxCard>
      <ValidationObserver ref="observer">
        <div class="duplicate-logs-details__fields">
          <OmxField
              :label="$t('duplicate-logs-details-view.source-date-title')"
              view-only
          >
            <template #value>{{ formattedSourceDate }}</template>
          </OmxField>

          <OmxField
              type="date"
              v-model="destinationDate"
              :label="$t('duplicate-logs-details-view.destination-date-title')"
              :placeholder="$t('duplicate-logs-details-view.destination-date-title')"
              :max-date="maxSelectableDate"
              rules="required"
              required
          />
        </div>
      </ValidationObserver>

      <template #foot>
        <div class="duplicate-logs-details__actions">
          <b-button icon-left="close-thick" tabindex="31" @click="cancelClicked">
            {{ $t('common.cancel') }}
          </b-button>
          <OmxPermissionButton
              :allowed="currentUserAccess('ProjectFilesDetails', UserActions.UPDATE, project_id)"
              type="is-primary" icon-left="content-duplicate" tabindex="32"
              @click="duplicateAllLogs">
            {{ $t('duplicate-logs-details-view.duplicate-now-button') }}
          </OmxPermissionButton>
        </div>
      </template>
    </OmxCard>
  </div>
</template>

<script>
import firebase from "firebase/app";
import { ValidationObserver } from "vee-validate";
import OmxPageHeader from "@/components/OmxPageHeader.vue";
import OmxCard from "@/components/OmxCard.vue";
import OmxField from "@/components/OmxField.vue";

export default {
  name: "DuplicateLogsDetails",
  components: { ValidationObserver, OmxPageHeader, OmxCard, OmxField },
  props: {
    project_id: { type: String },
    passed_source_date: { type: Date }
  },
  data() {
    return {
      sourceDate: this.passed_source_date,
      destinationDate: new Date(),
      isLoading: false
    }
  },
  methods: {
    async duplicateAllLogs() {
      const valid = await this.$refs.observer.validate();
      if (!valid) return;
      // Logs can't be duplicated into a future day.
      if (this.destinationDate > this.maxSelectableDate) {
        this.showError(this.$t('messages.future-date-error'));
        return;
      }
      this.isLoading = true;
      const sourceIsoDateString = this.convertToIso(this.sourceDate);
      const destinationIsoDateString = this.convertToIso(this.destinationDate);
      const functions = firebase.app().functions(this.$brand.functionsRegion);
      const duplicateLogsFunction = functions.httpsCallable(this.getSubmitEndpoint());
      duplicateLogsFunction({ action: "copy_logs", document_id: this.project_id, from: sourceIsoDateString, to: destinationIsoDateString })
          .then((result) => {
            this.isLoading = false;
            const data = result.data;
            if (data.failed > 0 && Array.isArray(data.errors)) {
              let message = `${this.$t('duplicate-logs-details-view.duplicate-report-successful', { count: data.successful })}\n`;
              message += `${this.$t('duplicate-logs-details-view.duplicate-report-failed', { count: data.failed })}\n`;
              message += this.$t('duplicate-logs-details-view.duplicate-report-errors') + ':\n';
              const uniqueErrorMessages = new Set();
              data.errors.forEach((err) => {
                let errorMsg = err.error;
                if (errorMsg === 'NoEstimationFound()') {
                  errorMsg = this.$t('duplicate-logs-details-view.error-NoEstimationFound');
                } else if (errorMsg === 'LogPriorToEstimationError()') {
                  errorMsg = this.$t('duplicate-logs-details-view.error-LogPriorToEstimationError');
                }
                uniqueErrorMessages.add(errorMsg);
              });
              uniqueErrorMessages.forEach((msg) => {
                message += `- ${msg}\n`;
              });
              this.$omxAlert({
                title: this.$t('duplicate-logs-details-view.title').toString(),
                message: message.replace(/\n/g, '<br>'),
                type: 'is-warning',
                icon: 'alert',
                confirmText: this.$t('common.ok').toString()
              });
            } else {
              this.showSuccess(this.$t('duplicate-logs-details-view.duplicate-report-successful', { count: data.successful }));
            }
            this.$emit('logsDuplicated');
          })
          .catch((error) => {
            this.isLoading = false;
            this.showError(this.$t('duplicate-logs-details-view.common-error'))
            console.error("Error duplicating logs: ", error?.code);
          });
    },
    convertToIso(dateToConvert) {
      const year = dateToConvert.getFullYear();
      const month = String(dateToConvert.getMonth() + 1).padStart(2, '0');
      const day = String(dateToConvert.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}T00:00:00Z`;
    },
    cancelClicked() {
      this.$emit('close');
    }
  },
  computed: {
    pageTitle() {
      return this.$t('duplicate-logs-details-view.title')
    },
    // Logs can't be dated in the future: cap the destination picker at end of today.
    maxSelectableDate() {
      const d = new Date();
      d.setHours(23, 59, 59, 999);
      return d;
    },
    formattedSourceDate() {
      if (!this.sourceDate) return "";
      const locale = this.$i18n.locale === 'fr' ? 'fr-FR' : 'en-GB';
      return new Intl.DateTimeFormat(locale, {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
      }).format(this.sourceDate);
    }
  }
};
</script>

<style scoped lang="scss">
@import "@/assets/scss/tokens";

.duplicate-logs-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.duplicate-logs-details__fields {
  display: grid;
  gap: 14px;
}

.duplicate-logs-details__actions {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  flex-wrap: wrap;
}
</style>
